import { PROTOCOL_VERSION } from '../shared/protocol-version.js';
import { CAR_BY_ID } from '../shared/cars.js';
import { validateMapSet } from '../shared/map-cache.js';
import { normaliseCrewModule } from '../shared/crew-modules.js';
import type { PersistedRoomState } from '../shared/race-session-state.js';
import { parsePersistedRoomState } from '../shared/persisted-room-state.js';
import { localRaceSettings, type LocalRaceSetup } from './local-race-protocol.js';

export interface StoredOfflineRace {
  version: 1;
  protocolVersion: number;
  generation: number;
  setup: LocalRaceSetup;
  checkpoint: PersistedRoomState;
  savedAt: number;
}

const DATABASE_NAME = 'nightshift-local-races';
const DATABASE_VERSION = 1;
const STORE_NAME = 'checkpoints';
const CURRENT_RACE_KEY = 'current';
const MAX_AGE_MS = 24 * 60 * 60 * 1_000;
const LEGACY_OFFLINE_RACE_TOMBSTONE_KEY = 'nightshift:offline-race-tombstone:v1';
export const OFFLINE_RACE_TOMBSTONE_KEY = 'nightshift:offline-race-tombstone:v2';
export const OFFLINE_DATABASE_TIMEOUT_MS = 2_500;
const MAX_DELETION_TOMBSTONES = 16;

interface OfflineRaceDeletionTombstone {
  version: 1;
  roomId: string;
  deletedAt: number;
  /** The finished attempt. A RACE AGAIN restarts the same room with a higher
   * attempt, and that new run must be saved again. Absent for discards. */
  raceAttempt?: number;
}

interface OfflineRaceGenerationMarker {
  version: 1;
  kind: 'generation';
  generation: number;
  /** Set when a race finished: a RACE AGAIN in that room (a higher attempt)
   * is a new run and claims the next generation. */
  finishedRoomId?: string;
  finishedAttempt?: number;
}

/** IndexedDB opens are asynchronous and may resolve out of call order. Keeping
 * writes on one queue guarantees that a finish/delete tombstone always wins
 * over every checkpoint save that was already in flight. */
export class OfflineRaceMutationQueue {
  private tail: Promise<void> = Promise.resolve();

  run<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.tail.then(operation, operation);
    this.tail = result.then(() => undefined, () => undefined);
    return result;
  }

  idle(): Promise<void> {
    return this.tail;
  }
}

const mutationQueue = new OfflineRaceMutationQueue();

export function saveOfflineRaceCheckpoint(
  setup: LocalRaceSetup,
  checkpoint: PersistedRoomState,
  now = Date.now(),
): Promise<boolean> {
  const attempt = checkpoint.raceAttempt ?? 1;
  if (checkpoint.phase === 'finished') writeDeletionTombstone(setup.roomId, now, attempt);
  return mutationQueue.run(async () => {
    if (checkpoint.phase === 'finished') {
      const deleted = await deleteOfflineRaceCheckpointNow(setup.roomId, attempt);
      if (deleted) clearDeletionTombstone(setup.roomId);
      return deleted;
    }
    const tombstone = readDeletionTombstone(setup.roomId);
    // A restarted attempt in the finished room claims a fresh generation, as
    // the finished one left only its generation marker behind.
    let restartedAttempt = false;
    if (tombstone) {
      if (tombstone.raceAttempt === undefined || attempt <= tombstone.raceAttempt) return false;
      clearDeletionTombstone(setup.roomId);
      restartedAttempt = true;
    }
    const record: StoredOfflineRace = {
      version: 1,
      protocolVersion: PROTOCOL_VERSION,
      generation: !restartedAttempt && positiveInteger(setup.offlineGeneration) ? Number(setup.offlineGeneration) : 0,
      setup,
      checkpoint,
      savedAt: now,
    };
    const validationRecord = record.generation > 0
      ? record
      : {
          ...record,
          generation: 1,
          setup: { ...record.setup, offlineGeneration: 1 },
        };
    if (!parseStoredOfflineRace(validationRecord, now)) return false;
    try {
      const committed = await withDatabase((database) => commitNewestCheckpoint(database, record));
      if (committed.accepted) setup.offlineGeneration = committed.generation;
      return committed.accepted;
    } catch {
      return false;
    }
  });
}

export function loadOfflineRaceCheckpoint(now = Date.now()): Promise<StoredOfflineRace | null> {
  return mutationQueue.run(async () => {
    try {
      const value = await withDatabase((database) => (
        requestResult<unknown>(database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(CURRENT_RACE_KEY))
      ));
      // An empty store is already clean. Avoid opening a second connection and
      // read/write transaction on every launch, especially on slow tablets.
      if (value === undefined || value === null) {
        pruneDeletionTombstones(now);
        return null;
      }
      if (isGenerationMarker(value)) {
        pruneDeletionTombstones(now);
        return null;
      }
      const storedRoomId = offlineCheckpointRoomId(value);
      const tombstone = storedRoomId ? readDeletionTombstone(storedRoomId) : null;
      if (tombstone && storedRoomId) {
        const deleted = await deleteOfflineRaceCheckpointNow(storedRoomId);
        if (deleted) clearDeletionTombstone(storedRoomId);
        return null;
      }
      const parsed = parseStoredOfflineRace(value, now);
      if (parsed) return parsed;
      if (storedRoomId) await deleteOfflineRaceCheckpointNow(storedRoomId);
      else await deleteInvalidOfflineRaceCheckpointNow();
      return null;
    } catch {
      return null;
    }
  });
}

/** `finishedAttempt` marks a race that ended on the line rather than a
 * discard, so a RACE AGAIN of the same room (a higher attempt) saves again. */
export function deleteOfflineRaceCheckpoint(expectedRoomId: string, finishedAttempt?: number): Promise<boolean> {
  if (!validId(expectedRoomId)) return Promise.resolve(false);
  const roomId = expectedRoomId;
  const attempt = positiveInteger(finishedAttempt) ? finishedAttempt : undefined;
  writeDeletionTombstone(roomId, Date.now(), attempt);
  return mutationQueue.run(async () => {
    const deleted = await deleteOfflineRaceCheckpointNow(roomId, attempt);
    if (deleted) clearDeletionTombstone(roomId);
    return deleted;
  });
}

async function deleteOfflineRaceCheckpointNow(expectedRoomId: string, finishedAttempt?: number): Promise<boolean> {
  try {
    return await withDatabase((database) => deleteOwnedCheckpoint(database, expectedRoomId, finishedAttempt));
  } catch {
    // IndexedDB can be unavailable in hardened/private contexts. In-memory
    // offline play remains usable; a durable tombstone prevents a completed
    // checkpoint from resurfacing if storage recovers on the next launch.
    return false;
  }
}

async function deleteInvalidOfflineRaceCheckpointNow(): Promise<boolean> {
  try {
    return await withDatabase((database) => deleteCurrentCheckpoint(database));
  } catch {
    return false;
  }
}

export function parseStoredOfflineRace(value: unknown, now = Date.now()): StoredOfflineRace | null {
  if (
    !isRecord(value)
    || value.version !== 1
    || !Number.isInteger(value.protocolVersion)
    || Number(value.protocolVersion) < 1
    || Number(value.protocolVersion) > PROTOCOL_VERSION
  ) return null;
  if (!isRecord(value.setup) || !isRecord(value.checkpoint)) return null;
  const setup = value.setup;
  const checkpoint = value.checkpoint;
  const player = setup.player;
  const payload = setup.payload;
  const map = setup.map;
  const savedAt = Number(value.savedAt);
  // Wall clocks can move backwards after Android time synchronisation or a
  // manual correction. Keep an otherwise valid recent checkpoint and
  // normalise its ordering timestamp instead of deleting the whole race.
  const normalisedSavedAt = savedAt > now ? now : savedAt;
  const generation = positiveInteger(value.generation)
    ? Number(value.generation)
    : positiveInteger(setup.offlineGeneration) ? Number(setup.offlineGeneration) : 1;
  if (
    !Number.isFinite(savedAt)
    || (value.generation !== undefined && !positiveInteger(value.generation))
    || savedAt > now + MAX_AGE_MS
    || now - savedAt > MAX_AGE_MS
    || !validId(setup.roomId)
    || (setup.offlineGeneration !== undefined && !positiveInteger(setup.offlineGeneration))
    || !isRecord(player)
    || !validId(player.id)
    || !validId(player.carId)
    || !validToken(player.token)
    || !validText(player.name, 24)
    || !CAR_BY_ID.has(player.carId)
    || !validVisualTuning(player.tuning)
    || !isRecord(payload)
    || !validId(payload.mapId)
    || !isRecord(map)
    || !validId(map.id)
    || payload.mapId !== map.id
    || !validateMapSet([map])
    || !validLocalPayload(payload, player, map.id)
    || !validToken(setup.inviteToken)
    || checkpoint.id !== setup.roomId
    || checkpoint.hostPlayerId !== player.id
    || checkpoint.phase === 'finished'
  ) return null;
  const parsedSetup = setup as unknown as LocalRaceSetup;
  const parsedCheckpoint = parsePersistedRoomState(checkpoint, parsedSetup.roomId);
  if (
    !parsedCheckpoint
    || !validStoredCheckpoint(checkpoint, parsedSetup.player.id, parsedSetup.map.id)
    || !checkpointMatchesSetup(parsedSetup, parsedCheckpoint)
  ) return null;
  if (
    value.protocolVersion === PROTOCOL_VERSION
    && value.generation === generation
    && setup.offlineGeneration === generation
    && value.savedAt === normalisedSavedAt
    && (value.checkpoint as unknown) === parsedCheckpoint
  ) {
    return value as unknown as StoredOfflineRace;
  }
  return {
    ...value,
    protocolVersion: PROTOCOL_VERSION,
    generation,
    savedAt: normalisedSavedAt,
    setup: {
      ...parsedSetup,
      offlineGeneration: generation,
    },
    checkpoint: parsedCheckpoint,
  } as unknown as StoredOfflineRace;
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB unavailable'));
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    let settled = false;
    const timeout = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Offline checkpoint store did not open in time'));
    }, OFFLINE_DATABASE_TIMEOUT_MS);
    request.addEventListener('upgradeneeded', () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME);
    });
    request.addEventListener('success', () => {
      if (settled) {
        request.result.close();
        return;
      }
      settled = true;
      globalThis.clearTimeout(timeout);
      resolve(request.result);
    }, { once: true });
    request.addEventListener('error', () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      reject(request.error ?? new Error('Unable to open offline checkpoint store'));
    }, { once: true });
    request.addEventListener('blocked', () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      reject(new Error('Offline checkpoint store is blocked'));
    }, { once: true });
  });
}

async function withDatabase<T>(operation: (database: IDBDatabase) => Promise<T>): Promise<T> {
  const database = await openDatabase();
  try {
    return await operation(database);
  } finally {
    database.close();
  }
}

function commitNewestCheckpoint(
  database: IDBDatabase,
  record: StoredOfflineRace,
): Promise<{ accepted: boolean; generation: number }> {
  return checkpointMutation(database, (store, current) => {
    const currentRoomId = offlineCheckpointRoomId(current);
    const currentGeneration = offlineCheckpointGeneration(current) ?? 0;
    let generation = positiveInteger(record.generation) ? record.generation : 0;
    if (
      isGenerationMarker(current)
      && current.finishedRoomId === record.setup.roomId
      && current.finishedAttempt !== undefined
      && (record.checkpoint.raceAttempt ?? 1) > current.finishedAttempt
    ) {
      generation = currentGeneration + 1;
    } else if (generation === 0) {
      generation = currentRoomId === record.setup.roomId
        ? Math.max(1, currentGeneration)
        : Math.max(1, currentGeneration + 1);
    }
    const claimed: StoredOfflineRace = {
      ...record,
      generation,
      setup: {
        ...record.setup,
        offlineGeneration: generation,
      },
    };
    if (!offlineCheckpointCanReplace(current, claimed)) {
      return { accepted: false, generation };
    }
    store.put(claimed, CURRENT_RACE_KEY);
    return { accepted: true, generation };
  });
}

function deleteOwnedCheckpoint(
  database: IDBDatabase,
  expectedRoomId: string,
  finishedAttempt?: number,
): Promise<boolean> {
  return checkpointMutation(database, (store, current) => {
    if (current === undefined || current === null) return true;
    if (isGenerationMarker(current)) return true;
    if (offlineCheckpointRoomId(current) !== expectedRoomId) {
      // Another tab has already published a newer race. Reaching this state is
      // success for the old owner, but its replacement must remain untouched.
      return true;
    }
    const generation = offlineCheckpointGeneration(current);
    if (generation) {
      store.put(finishedAttempt === undefined
        ? generationMarker(generation)
        : { ...generationMarker(generation), finishedRoomId: expectedRoomId, finishedAttempt }, CURRENT_RACE_KEY);
    } else {
      store.delete(CURRENT_RACE_KEY);
    }
    return true;
  });
}

function deleteCurrentCheckpoint(database: IDBDatabase): Promise<boolean> {
  return checkpointMutation(database, (store, current) => {
    const generation = offlineCheckpointGeneration(current);
    if (generation) store.put(generationMarker(generation), CURRENT_RACE_KEY);
    else store.delete(CURRENT_RACE_KEY);
    return true;
  });
}

function checkpointMutation<T>(
  database: IDBDatabase,
  operation: (store: IDBObjectStore, current: unknown) => T,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(CURRENT_RACE_KEY);
    let settled = false;
    let decided = false;
    let result!: T;
    const finish = (error?: Error): void => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      if (error) reject(error);
      else if (decided) resolve(result);
      else reject(new Error('Offline checkpoint transaction completed without a decision'));
    };
    const timeout = globalThis.setTimeout(() => {
      if (settled) return;
      try {
        transaction.abort();
      } catch {
        // A browser may already have auto-aborted the transaction without
        // delivering its event. The timeout still releases the mutation queue.
      }
      finish(new Error('Offline checkpoint transaction timed out'));
    }, OFFLINE_DATABASE_TIMEOUT_MS);
    transaction.addEventListener('complete', () => finish(), { once: true });
    transaction.addEventListener('abort', () => finish(transaction.error ?? new Error('Offline checkpoint transaction aborted')), { once: true });
    transaction.addEventListener('error', () => finish(transaction.error ?? new Error('Offline checkpoint transaction failed')), { once: true });
    request.addEventListener('success', () => {
      if (settled) return;
      try {
        result = operation(store, request.result);
        decided = true;
      } catch (error) {
        try {
          transaction.abort();
        } catch {
          // finish() below still releases the queue if abort cannot emit.
        }
        finish(error instanceof Error ? error : new Error('Offline checkpoint transaction failed'));
      }
    }, { once: true });
    request.addEventListener('error', () => {
      finish(request.error ?? new Error('Offline checkpoint transaction failed'));
    }, { once: true });
  });
}

/** Keeps a newer offline race authoritative even when an older tab continues
 * emitting checkpoints after another tab has started a replacement. */
export function offlineCheckpointCanReplace(current: unknown, candidate: StoredOfflineRace): boolean {
  const currentRoomId = offlineCheckpointRoomId(current);
  const currentGeneration = offlineCheckpointGeneration(current);
  const candidateGeneration = offlineCheckpointGeneration(candidate);
  if (!currentRoomId) {
    return currentGeneration !== null && candidateGeneration !== null
      ? candidateGeneration > currentGeneration
      : true;
  }
  if (currentRoomId === candidate.setup.roomId) {
    if (
      currentGeneration !== null
      && candidateGeneration !== null
      && currentGeneration !== candidateGeneration
    ) return false;
    const currentOrder = offlineCheckpointOrder(current);
    const candidateOrder = offlineCheckpointOrder(candidate);
    if (!currentOrder) return true;
    if (!candidateOrder) return false;
    return compareCheckpointOrder(candidateOrder, currentOrder) >= 0;
  }
  if (currentGeneration !== null && candidateGeneration !== null) {
    return candidateGeneration > currentGeneration;
  }
  const currentCreatedAt = offlineCheckpointCreatedAt(current);
  const candidateCreatedAt = offlineCheckpointCreatedAt(candidate);
  if (currentCreatedAt === null) return true;
  if (candidateCreatedAt === null) return false;
  return candidateCreatedAt > currentCreatedAt;
}

function offlineCheckpointGeneration(value: unknown): number | null {
  if (!isRecord(value)) return null;
  if (positiveInteger(value.generation)) return Number(value.generation);
  if (isRecord(value.setup) && positiveInteger(value.setup.offlineGeneration)) {
    return Number(value.setup.offlineGeneration);
  }
  return offlineCheckpointRoomId(value) ? 1 : null;
}

function generationMarker(generation: number): OfflineRaceGenerationMarker {
  return { version: 1, kind: 'generation', generation };
}

function isGenerationMarker(value: unknown): value is OfflineRaceGenerationMarker {
  return isRecord(value)
    && value.version === 1
    && value.kind === 'generation'
    && positiveInteger(value.generation);
}

type OfflineCheckpointOrder = readonly [
  raceAttempt: number,
  sequence: number,
  lastActivity: number,
  savedAt: number,
];

function offlineCheckpointOrder(value: unknown): OfflineCheckpointOrder | null {
  if (!isRecord(value) || !isRecord(value.checkpoint)) return null;
  const raceAttempt = value.checkpoint.raceAttempt ?? 1;
  if (
    !nonNegativeInteger(raceAttempt)
    || Number(raceAttempt) < 1
    || !nonNegativeInteger(value.checkpoint.sequence)
    || !finite(value.checkpoint.lastActivity)
    || !finite(value.savedAt)
  ) return null;
  return [
    Number(raceAttempt),
    Number(value.checkpoint.sequence),
    Number(value.checkpoint.lastActivity),
    Number(value.savedAt),
  ];
}

function compareCheckpointOrder(left: OfflineCheckpointOrder, right: OfflineCheckpointOrder): number {
  for (let index = 0; index < left.length; index += 1) {
    const difference = left[index]! - right[index]!;
    if (difference !== 0) return difference;
  }
  return 0;
}

function offlineCheckpointRoomId(value: unknown): string | null {
  if (!isRecord(value) || !isRecord(value.setup)) return null;
  return validId(value.setup.roomId) ? value.setup.roomId : null;
}

function offlineCheckpointCreatedAt(value: unknown): number | null {
  if (!isRecord(value) || !isRecord(value.checkpoint)) return null;
  return finite(value.checkpoint.createdAt) ? Number(value.checkpoint.createdAt) : null;
}

function writeDeletionTombstone(roomId: string, deletedAt = Date.now(), raceAttempt?: number): void {
  if (!validId(roomId) || typeof localStorage === 'undefined') return;
  try {
    pruneDeletionTombstones(deletedAt);
    const tombstone: OfflineRaceDeletionTombstone = raceAttempt === undefined
      ? { version: 1, roomId, deletedAt }
      : { version: 1, roomId, deletedAt, raceAttempt };
    localStorage.setItem(deletionTombstoneKey(roomId), JSON.stringify(tombstone));
  } catch {
    // IndexedDB may still delete the checkpoint; the marker is an extra layer.
  }
}

function readDeletionTombstone(roomId: string): OfflineRaceDeletionTombstone | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const current = parseDeletionTombstone(localStorage.getItem(deletionTombstoneKey(roomId)));
    if (current?.roomId === roomId) return current;
    const legacy = parseDeletionTombstone(localStorage.getItem(LEGACY_OFFLINE_RACE_TOMBSTONE_KEY));
    return legacy?.roomId === roomId ? legacy : null;
  } catch {
    return null;
  }
}

function clearDeletionTombstone(roomId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(deletionTombstoneKey(roomId));
    const legacy = parseDeletionTombstone(localStorage.getItem(LEGACY_OFFLINE_RACE_TOMBSTONE_KEY));
    if (legacy?.roomId === roomId) localStorage.removeItem(LEGACY_OFFLINE_RACE_TOMBSTONE_KEY);
  } catch {
    // A surviving marker is conservative: it suppresses only its own room.
  }
}

function deletionTombstoneKey(roomId: string): string {
  return `${OFFLINE_RACE_TOMBSTONE_KEY}:${roomId}`;
}

function parseDeletionTombstone(raw: string | null): OfflineRaceDeletionTombstone | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    if (
      !isRecord(value)
      || value.version !== 1
      || !validId(value.roomId)
      || !finite(value.deletedAt)
      || (value.raceAttempt !== undefined && !positiveInteger(value.raceAttempt))
    ) return null;
    return value as unknown as OfflineRaceDeletionTombstone;
  } catch {
    return null;
  }
}

function pruneDeletionTombstones(now = Date.now()): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const tombstones: Array<{ key: string; deletedAt: number }> = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(`${OFFLINE_RACE_TOMBSTONE_KEY}:`)) continue;
      const tombstone = parseDeletionTombstone(localStorage.getItem(key));
      if (!tombstone || now - tombstone.deletedAt > MAX_AGE_MS) {
        localStorage.removeItem(key);
        index -= 1;
        continue;
      }
      tombstones.push({ key, deletedAt: tombstone.deletedAt });
    }
    tombstones
      .sort((left, right) => right.deletedAt - left.deletedAt)
      .slice(MAX_DELETION_TOMBSTONES)
      .forEach(({ key }) => localStorage.removeItem(key));
  } catch {
    // Storage restrictions leave existing markers in their conservative state.
  }
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (result: { value: T } | { error: Error }): void => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeout);
      if ('error' in result) reject(result.error);
      else resolve(result.value);
    };
    const timeout = globalThis.setTimeout(() => {
      try {
        request.transaction?.abort();
      } catch {
        // Closing the database in withDatabase() remains the fallback.
      }
      finish({ error: new Error('Offline checkpoint read timed out') });
    }, OFFLINE_DATABASE_TIMEOUT_MS);
    request.addEventListener('success', () => finish({ value: request.result }), { once: true });
    request.addEventListener('error', () => finish({
      error: request.error ?? new Error('Offline checkpoint read failed'),
    }), { once: true });
  });
}

function validId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function validToken(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{8,200}$/.test(value);
}

function validText(value: unknown, maximumLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maximumLength;
}

function validLocalPayload(
  payload: Record<string, unknown>,
  player: Record<string, unknown>,
  mapId: string,
): boolean {
  const eventMode = payload.eventMode ?? (payload.timeTrial === true ? 'time-trial' : 'circuit');
  return payload.mapId === mapId
    && payload.carId === player.carId
    && validText(payload.name, 24)
    && Number.isInteger(payload.npcCount)
    && Number(payload.npcCount) >= 0
    && Number(payload.npcCount) <= 11
    && ['rookie', 'sport', 'pro'].includes(String(payload.npcDifficulty))
    && typeof payload.antiCheat === 'boolean'
    && typeof payload.collisions === 'boolean'
    && (payload.traffic === undefined || typeof payload.traffic === 'boolean')
    && (payload.timeTrial === undefined || typeof payload.timeTrial === 'boolean')
    && ['circuit', 'sprint', 'time-trial', 'style-rush', 'elimination', 'heat-run', 'road-rage'].includes(String(eventMode))
    && (payload.wager === undefined
      || ['clean-finish', 'clean-passes', 'extreme-route'].includes(String(payload.wager)))
    && (payload.dailyChallengeId === undefined
      || (typeof payload.dailyChallengeId === 'string'
        && /^\d{4}-\d{2}-\d{2}$/.test(payload.dailyChallengeId)))
    && (payload.signatureActive === undefined || typeof payload.signatureActive === 'boolean')
    && (payload.arcadePreset === undefined
      || ['standard', 'nitro-rush', 'heavy-metal', 'traffic-panic', 'low-gravity'].includes(String(payload.arcadePreset)))
    && validVisualTuning(payload.tuning);
}

function validStoredCheckpoint(
  checkpoint: Record<string, unknown>,
  playerId: string,
  mapId: string,
): boolean {
  if (
    !['lobby', 'countdown', 'racing'].includes(String(checkpoint.phase))
    || !finite(checkpoint.createdAt)
    || !finite(checkpoint.expiresAt)
    || !finite(checkpoint.lastActivity)
    || !finite(checkpoint.countdownUntil)
    || !nonNegativeInteger(checkpoint.sequence)
    || !nonNegativeInteger(checkpoint.projectileSequence)
    || !nonNegativeInteger(checkpoint.impactSequence)
    || !isRecord(checkpoint.settings)
    || checkpoint.settings.mapId !== mapId
    || !Number.isInteger(checkpoint.settings.npcCount)
    || !['rookie', 'sport', 'pro'].includes(String(checkpoint.settings.npcDifficulty))
    || !Number.isInteger(checkpoint.settings.maxPlayers)
    || !Array.isArray(checkpoint.racers)
    || checkpoint.racers.length < 1
    || checkpoint.racers.length > 16
    || !Array.isArray(checkpoint.projectiles)
    || !Array.isArray(checkpoint.impacts)
    || checkpoint.projectiles.length > 128
    || checkpoint.impacts.length > 256
  ) return false;

  let matchingPlayer = false;
  const racerIds = new Set<string>();
  for (const candidate of checkpoint.racers) {
    if (!isRecord(candidate) || !validStoredRacer(candidate) || racerIds.has(candidate.id as string)) return false;
    racerIds.add(candidate.id as string);
    if (candidate.id === playerId && candidate.npc === false) matchingPlayer = true;
  }
  return matchingPlayer;
}

function validStoredRacer(racer: Record<string, unknown>): boolean {
  return validId(racer.id)
    && validText(racer.name, 24)
    && validId(racer.carId)
    && CAR_BY_ID.has(racer.carId)
    && typeof racer.npc === 'boolean'
    && validVisualTuning(racer.tuning)
    && finite(racer.x)
    && finite(racer.y)
    && finite(racer.z)
    && finite(racer.yaw)
    && finite(racer.velocityX)
    && finite(racer.velocityZ)
    && finite(racer.verticalSpeed)
    && nonNegativeInteger(racer.lap)
    && nonNegativeInteger(racer.checkpoint)
    && finite(racer.progress)
    && finite(racer.health);
}

function checkpointMatchesSetup(
  setup: LocalRaceSetup,
  checkpoint: PersistedRoomState,
): boolean {
  const expectedSettings = localRaceSettings(setup.payload, setup.map);
  const settingKeys = [
    'mapId',
    'npcCount',
    'npcDifficulty',
    'maxPlayers',
    'antiCheat',
    'collisions',
    'traffic',
    'timeOfDay',
    'weather',
    'timeTrial',
    'eventMode',
    'arcadePreset',
    'tourId',
    'tourStage',
    'featuredRival',
    'wager',
    'dailyChallengeId',
    'rewindLimit',
    'laps',
  ] as const;
  if (settingKeys.some((key) => checkpoint.settings[key] !== expectedSettings[key])) return false;
  if (
    setup.payload.carId !== setup.player.carId
    || !sameVisualTuning(setup.payload.tuning, setup.player.tuning)
  ) return false;
  const host = checkpoint.racers.find((racer) => racer.id === setup.player.id && !racer.npc);
  return Boolean(host)
    && host!.token === setup.player.token
    && host!.name === setup.player.name
    && host!.carId === setup.player.carId
    && host!.crewModule === normaliseCrewModule(setup.player.crewModule)
    && host!.signatureActive === (setup.player.signatureActive === true)
    && sameVisualTuning(host!.tuning, setup.player.tuning);
}

function sameVisualTuning(left: unknown, right: unknown): boolean {
  if (!isRecord(left) || !isRecord(right)) return false;
  return [
    'paint',
    'accent',
    'rims',
    'neon',
    'neonEnabled',
    'spoiler',
    'wheels',
    'decal',
    'stance',
  ].every((key) => left[key] === right[key]);
}

function validVisualTuning(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return ['paint', 'accent', 'rims', 'neon'].every((key) => (
    typeof value[key] === 'string' && /^#[0-9a-f]{6}$/i.test(value[key] as string)
  ))
    && typeof value.neonEnabled === 'boolean'
    && ['none', 'ducktail', 'gt'].includes(String(value.spoiler))
    && ['turbine', 'mesh', 'five-spoke'].includes(String(value.wheels))
    && ['none', 'center-stripe', 'twin-stripe', 'chevron', 'circuit'].includes(String(value.decal))
    && finite(value.stance)
    && Number(value.stance) >= -0.08
    && Number(value.stance) <= 0.08;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function nonNegativeInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}

function positiveInteger(value: unknown): value is number {
  return Number.isInteger(value)
    && Number(value) >= 1
    && Number(value) <= Number.MAX_SAFE_INTEGER;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
